import { useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField, Tooltip
} from '@mui/material';
import React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { AccountTree, BadgeOutlined, CellTower, Explore, Flag, MyLocation, Shield } from '@mui/icons-material';

import { useAuthContext } from '../../context/auth';
import { useUnitContext } from '../../context/unit';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDialogStyles from '../../hooks/useDialogStyles';
import { DialogHeader, FormSection, SubmitButton } from '../dialogKit';
import PositionPicker from '../map/PositionPicker';

// Unit types: 0 Command, 1 Regiment, 2 Company, 3 Outpost (same as Add.js)
const UNIT_TYPES = [
  { label: 'Command', Icon: Shield },
  { label: 'Regiment', Icon: AccountTree },
  { label: 'Company', Icon: Flag },
  { label: 'Outpost', Icon: CellTower },
];

const str = (v) => (v === null || v === undefined ? '' : String(v));

const Edit = ({ id, type, name, parentId, number, latitude, longitude, accuracy, trueNorth, distanceRate, units }) => {
  const axiosPrivate = useAxiosPrivate();
  const { dispatch } = useUnitContext();
  const { auth } = useAuthContext();
  const ds = useDialogStyles();

  const unitType = UNIT_TYPES[type] || { label: 'Unit', Icon: Shield };
  const label = unitType.label;
  const parentLabel = type > 0 ? UNIT_TYPES[type - 1].label : null;

  const [error, setError] = useState(null);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form values, filled from the unit each time the dialog opens
  const original = {
    name: str(name),
    parentId: str(parentId),
    number: str(number),
    latitude: str(latitude),
    longitude: str(longitude),
    accuracy: str(accuracy),
    trueNorth: str(trueNorth),
    distanceRate: str(distanceRate),
  };
  const [form, setForm] = useState(original);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const openDialog = () => {
    setForm(original);
    setError(null);
    setShow(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setShow(false);
    setError(null);
  };

  const handleUpdate = async () => {
    if (!auth) {
      setError('You must be logged in');
      return;
    }

    // Send only what changed; an emptied field keeps its old value (as before)
    const updateUnit = {};
    Object.keys(form).forEach((key) => {
      const value = str(form[key]).trim();
      if (value !== '' && value !== original[key]) updateUnit[key] = value;
    });

    if (Object.keys(updateUnit).length === 0) {
      setError('Nothing changed');
      return;
    }

    updateUnit.id = id;

    setSaving(true);
    try {
      const response = await axiosPrivate.patch('/api/units', updateUnit);
      dispatch({ type: 'UPDATE_UNIT', payload: response.data });
      setError(null);
      setShow(false);
    } catch (error) {
      setError(error.response?.data.error || `Unable to update the ${label.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const numberField = (fieldLabel, key, { unit, helper } = {}) => (
    <TextField
      size="small"
      label={fieldLabel}
      type="number"
      value={form[key]}
      onChange={set(key)}
      helperText={helper}
      InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
      sx={ds.fieldSx}
    />
  );

  const changed = Object.keys(form).some((k) => str(form[k]).trim() !== '' && str(form[k]).trim() !== original[k]);

  return (
    <>
      <Tooltip title={`Edit ${label.toLowerCase()}`}>
        <IconButton
          color="secondary"
          size="small"
          style={{ marginRight: 8 }}
          onClick={openDialog}
        >
          <EditIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={show}
        onClose={closeDialog}
        fullWidth
        maxWidth={type === 3 ? 'sm' : 'xs'}
        PaperProps={{ sx: ds.paperSx }}
      >
        <DialogHeader
          icon={unitType.Icon}
          title={`Edit ${label.toLowerCase()}`}
          subtitle={name}
          onClose={closeDialog}
          disabled={saving}
        />

        <DialogContent sx={ds.contentSx}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: type === 3 ? '1fr 1fr' : '1fr' }, gap: 2 }}>
            {type === 3 && <FormSection icon={BadgeOutlined} title="Identity" />}

            <TextField
              fullWidth
              size="small"
              label="Name"
              value={form.name}
              onChange={set('name')}
              autoComplete="off"
              sx={ds.fieldSx}
            />

            {type > 0 && (
              <FormControl fullWidth size="small" sx={ds.fieldSx}>
                <InputLabel>{`Parent ${parentLabel.toLowerCase()}`}</InputLabel>
                <Select
                  label={`Parent ${parentLabel.toLowerCase()}`}
                  value={form.parentId}
                  onChange={set('parentId')}
                  MenuProps={ds.menuProps}
                >
                  {units
                    ?.filter(unit => unit.type === type - 1)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(unit => (
                      <MenuItem key={unit._id} value={unit._id}>
                        {unit.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}

            {type === 3 && (
              <>
                {numberField('Outpost number', 'number', { helper: 'Number sent by the radar' })}
                <Box />

                <FormSection icon={MyLocation} title="Position" hint="type it or pick it on the map" />
                {numberField('Latitude', 'latitude', { unit: '°' })}
                {numberField('Longitude', 'longitude', { unit: '°' })}
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <PositionPicker
                    lat={form.latitude}
                    lng={form.longitude}
                    onChange={(la, ln) => setForm((f) => ({ ...f, latitude: String(la), longitude: String(ln) }))}
                    others={(units || [])
                      .filter((u) => u.type === 3 && String(u._id) !== String(id))
                      .map((u) => ({ id: u._id, name: u.name, latitude: u.latitude, longitude: u.longitude }))}
                  />
                </Box>

                <FormSection icon={Explore} title="Radar calibration" />
                {numberField('True north', 'trueNorth', { unit: '°' })}
                {numberField('Accuracy', 'accuracy')}
                {numberField('Distance rate', 'distanceRate', { helper: 'Multiplier for radar distances' })}
              </>
            )}
          </Box>

          {error && (
            <Alert severity={error === 'Nothing changed' ? 'info' : 'error'} sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={ds.actionsSx}>
          <Button onClick={closeDialog} disabled={saving} sx={ds.cancelButtonSx}>
            Cancel
          </Button>
          <SubmitButton
            label="Save changes"
            busyLabel="Saving…"
            busy={saving}
            disabled={!changed}
            onClick={handleUpdate}
            icon={<unitType.Icon />}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
